import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Chart } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';

import { SkillAiTimeseriesItem } from '../../../models/skill-learning.model';

export type AiTimeseriesMetric = 'requests' | 'tokens' | 'cost';

@Component({
  selector: 'app-ai-statistics-timeseries',
  templateUrl: './ai-statistics-timeseries.component.html',
  styleUrls: ['./ai-statistics-timeseries.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiStatisticsTimeseriesComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() items: SkillAiTimeseriesItem[] = [];
  @Input() metric: AiTimeseriesMetric = 'requests';

  @ViewChild('chartCanvas') private chartCanvas: ElementRef;

  private chart: Chart | null = null;

  constructor(private translate: TranslateService) {}

  ngAfterViewInit(): void {
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items || changes.metric) {
      this.renderChart();
    }
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private renderChart(): void {
    if (!this.chartCanvas) {
      return;
    }
    if (this.chart) {
      this.updateChart();
      return;
    }
    this.chart = new Chart(this.getChartContext(), this.buildChartConfig());
  }

  private updateChart(): void {
    if (!this.chart) {
      return;
    }
    this.chart.data.labels = this.buildLabels();
    this.chart.data.datasets[0].data = this.buildData();
    this.chart.data.datasets[0].label = this.translate.instant(this.resolveMetricLabelKey());
    this.chart.update();
  }

  private buildChartConfig(): object {
    return {
      type: 'line',
      data: {
        labels: this.buildLabels(),
        datasets: [this.buildDataset()],
      },
      options: this.buildChartOptions(),
    };
  }

  private buildDataset(): object {
    return {
      label: this.translate.instant(this.resolveMetricLabelKey()),
      data: this.buildData(),
      borderColor: '#d91015',
      backgroundColor: 'rgba(217, 16, 21, 0.12)',
      fill: true,
      lineTension: 0.2,
      borderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 4,
      pointBackgroundColor: '#d91015',
    };
  }

  private buildChartOptions(): object {
    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true,
          },
        }],
      },
    };
  }

  private buildLabels(): string[] {
    return this.items.map(item => item.date);
  }

  private buildData(): number[] {
    return this.items.map(item => this.resolveMetricValue(item));
  }

  private resolveMetricValue(item: SkillAiTimeseriesItem): number {
    if (this.metric === 'tokens') {
      return item.totalTokens;
    }
    if (this.metric === 'cost') {
      return item.estimatedCost;
    }
    return item.totalRequests;
  }

  private resolveMetricLabelKey(): string {
    if (this.metric === 'tokens') {
      return 'SKILL_LEARNING_AI_STATS_CHART_METRIC_TOKENS';
    }
    if (this.metric === 'cost') {
      return 'SKILL_LEARNING_AI_STATS_CHART_METRIC_COST';
    }
    return 'SKILL_LEARNING_AI_STATS_CHART_METRIC_REQUESTS';
  }

  private getChartContext(): CanvasRenderingContext2D {
    return this.chartCanvas.nativeElement.getContext('2d') as CanvasRenderingContext2D;
  }

  private destroyChart(): void {
    if (!this.chart) {
      return;
    }
    this.chart.destroy();
    this.chart = null;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/ai-statistics-timeseries/ai-statistics-timeseries.component.ts